import {Fragment, useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useOrderDetails} from '../../api/order';
import {APIResponseType, HttpResponse, OrderDetail} from '../../api/models';
import {Box, Button, CircularProgress, IconButton, Snackbar, Typography} from '@mui/material';
import {useEventBatching} from '../../api/events';
import CloseIcon from '@mui/icons-material/Close';
import {OrderDetails} from './components/OrderDetails';

interface OrderDetailsLoaderProps {
    isLoading: boolean;
    error?: string; // Optional error message
    pastDelay?: boolean; // Indicate if a minimum loading time has passed (for smoother UX)
}
const OrderDetailsLoader: React.FC<OrderDetailsLoaderProps> = ({isLoading, error, pastDelay = false}) => {
    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                }}>
                <CircularProgress />
                {pastDelay && (
                    <Typography variant='body2' sx={{marginTop: 2}}>
                        Loading order details...
                    </Typography>
                )}
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                }}>
                <Typography variant='body1' color='error'>
                    Error: {error}
                </Typography>
            </Box>
        );
    }

    return null;
};

export const OrderDetailScreen = () => {
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get('orderId');
    const [snackBarMessage, updateSnackbarMessage] = useState<string>('');
    const [open, setOpen] = useState(false);

    const {trackEvent} = useEventBatching();

    const {data, isLoading, error} = useOrderDetails(orderId);

    const handleClose = () => setOpen(false);

    const throwSnackbarMessage = (message: string) => {
        updateSnackbarMessage(message);
        setOpen(true);
    };

    useEffect(() => {
        if (error) {
            trackEvent({
                eventName: 'Fetch Order List: Failed',
                props: {
                    errorName: error.name,
                    errorMessage: error.message,
                    errorStack: error.stack ? JSON.stringify(error.stack) : '',
                },
            });
        }
    }, [error]);

    useEffect(() => {
        if (data?.type === APIResponseType.ErrorString) {
            const errorMessage = (data.data as HttpResponse).message;
            throwSnackbarMessage(errorMessage);
        }
    }, [data]);

    const action = (
        <Fragment>
            <Button size='small' onClick={handleClose}>
                Dismiss
            </Button>
            <IconButton size='small' aria-label='close' color='inherit' onClick={handleClose}>
                <CloseIcon fontSize='small' />
            </IconButton>
        </Fragment>
    );

    let element = <></>;
    if (data) {
        if (data.type === APIResponseType.OrderDetails) {
            element = (
                <>
                    <OrderDetails order={data.data as OrderDetail} />
                </>
            );
        } else {
            /**
             * render error string component?
             */
        }
    } else if (isLoading) {
        element = <OrderDetailsLoader isLoading={isLoading} />;
    } else {
        /**
         * build a details not found screen
         * if the order details or order id is not found, what should be the response?
         * (loading state? error state? screen not found, redirect back?)
         */
        element = <></>;
    }

    return (
        <>
            {element}
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={open}
                action={action}
                autoHideDuration={2500}
                message={snackBarMessage}
            />
        </>
    );
};
